import type { AutofillProposal } from '@reditelji/shared';
import { useEffect } from 'react';
import { gapKey } from './planReview';

export function typingInto(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
}

/**
 * Enter on a focused control belongs to that control: a button presses, a
 * combobox opens. A board key bound to Enter has to stay out of its way.
 */
export function onControl(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el?.closest) return false;
  return el.closest('button, a[href], [role="combobox"], [role="option"]') !== null;
}

/**
 * Walk the plan from the keyboard: J/K step through it in review order,
 * Enter takes the one in focus and Backspace leaves it. Acting on a proposal
 * moves focus to the next one, so a run of decisions never touches the mouse.
 */
export function usePlanKeys({
  enabled = true,
  walk,
  focused,
  onFocus,
  onAccept,
  onReject,
}: {
  /** Off while the side panel's own list has the keys (a selected gap). */
  enabled?: boolean;
  /** Empty while no plan is up — the keys then do nothing. */
  walk: AutofillProposal[];
  focused: string | null;
  onFocus: (key: string | null) => void;
  onAccept: (p: AutofillProposal) => void;
  onReject: (p: AutofillProposal) => void;
}) {
  useEffect(() => {
    if (!enabled || walk.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (typingInto(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      const at = walk.findIndex((p) => gapKey(p.gap) === focused);
      const step = (d: number) => {
        const next = at === -1 ? (d > 0 ? 0 : walk.length - 1) : (at + d + walk.length) % walk.length;
        onFocus(gapKey(walk[next]!.gap));
      };
      const act = (run: (p: AutofillProposal) => void) => {
        if (at === -1) return;
        const after = walk[at + 1] ?? walk[at - 1];
        run(walk[at]!);
        onFocus(after ? gapKey(after.gap) : null);
      };

      switch (e.key) {
        case 'j':
        case 'ArrowDown':
          e.preventDefault();
          step(1);
          break;
        case 'k':
        case 'ArrowUp':
          e.preventDefault();
          step(-1);
          break;
        case 'Enter':
          if (onControl(e.target)) break;
          if (at !== -1) e.preventDefault();
          act(onAccept);
          break;
        case 'Backspace':
        case 'Delete':
          if (at !== -1) e.preventDefault();
          act(onReject);
          break;
        case 'Escape':
          onFocus(null);
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, walk, focused, onFocus, onAccept, onReject]);
}

/** N moves the side panel to the next uncovered stretch of the day. */
export function useNextTargetKey({ enabled, onNext }: { enabled: boolean; onNext: () => void }) {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (typingInto(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key !== 'n' && e.key !== 'N') return;
      e.preventDefault();
      onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, onNext]);
}
